import { OFFICIAL_BOOKING_URL } from "@/lib/data";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useCustomerAuth } from "@/lib/customerAuth";

const links = [
    { to: "/rooms", label: "Stay" },
    { to: "/amenities", label: "Amenities" },
    { to: "/gallery", label: "Gallery" },
    { to: "/experiences", label: "Experiences" },
    { to: "/local-area", label: "Local Area" },
    { to: "/offers", label: "Offers" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { user } = useCustomerAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 overflow-x-clip transition-all duration-500 ${
                scrolled ? "glass border-b border-white/40 py-4" : "bg-transparent py-7"
            }`}
        >
            <div className={`max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-20 flex items-center justify-between gap-4 transition-all duration-500 ${scrolled ? "min-h-[68px]" : "min-h-[92px]"}`}>
                {/* LEFT · Brand anchor */}
                <Link to="/" data-testid="nav-brand" className="flex items-center gap-3 xl:gap-4 group shrink-0 min-w-0">
                    <span className={`hidden sm:inline-block w-6 xl:w-10 h-[1px] transition-colors ${scrolled ? "bg-navy/60" : "bg-ivory/70"}`} />
                    <span
                        className={`font-display text-xl sm:text-2xl xl:text-3xl tracking-[0.02em] leading-none whitespace-nowrap transition-colors ${
                            scrolled ? "text-navy" : "text-ivory"
                        }`}
                        style={{ fontWeight: 500 }}
                    >
                        Serenity <span className="text-gold font-serif-italic">Cove</span>
                    </span>
                </Link>

                {/* RIGHT · Nav + CTA */}
                <div className="flex items-center min-w-0">
                    <nav className="hidden xl:flex items-center gap-x-4 2xl:gap-x-9 mr-4 2xl:mr-12 min-w-0">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[11px] 2xl:text-[12px] uppercase tracking-[0.18em] 2xl:tracking-[0.28em] font-light leading-none whitespace-nowrap transition-colors ${
                                        scrolled
                                            ? isActive
                                                ? "text-gold"
                                                : "text-navy/80 hover:text-gold"
                                            : isActive
                                            ? "text-gold"
                                            : "text-ivory/85 hover:text-gold"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                    <Link to="/booking"
                        data-testid="nav-book-cta"
                        className="hidden xl:inline-flex btn-gold px-6 2xl:px-10 py-3 rounded-full items-center leading-none whitespace-nowrap text-[11px] 2xl:text-[12px]"
                    >
                        Book Now
                    </Link>
                    {user ? (
                        <Link
                            to="/account"
                            data-testid="nav-account"
                            className={`hidden xl:inline-flex items-center gap-2 ml-3 px-4 py-2.5 rounded-full border transition-colors text-[11px] uppercase tracking-[0.18em] whitespace-nowrap ${
                                scrolled ? "border-navy/25 text-navy hover:bg-navy hover:text-ivory" : "border-ivory/50 text-ivory hover:bg-ivory hover:text-navy"
                            }`}
                        >
                            <User size={13} /> {user.name?.split(" ")[0] || "Account"}
                        </Link>
                    ) : (
                        <div className="hidden xl:flex items-center ml-3 gap-1">
                            <Link
                                to="/signin"
                                data-testid="nav-signin"
                                className={`inline-flex items-center px-3 py-2.5 rounded-full transition-colors text-[11px] uppercase tracking-[0.18em] whitespace-nowrap ${
                                    scrolled ? "text-navy hover:text-gold" : "text-ivory/85 hover:text-gold"
                                }`}
                            >
                                Sign in
                            </Link>
                            <span className={`text-[10px] ${scrolled ? "text-navy/30" : "text-ivory/40"}`}>·</span>
                            <Link
                                to="/signup"
                                data-testid="nav-signup"
                                className={`inline-flex items-center px-3 py-2.5 rounded-full transition-colors text-[11px] uppercase tracking-[0.18em] whitespace-nowrap ${
                                    scrolled ? "text-navy hover:text-gold" : "text-ivory/85 hover:text-gold"
                                }`}
                            >
                                Sign up
                            </Link>
                        </div>
                    )}
                    <button
                        data-testid="nav-mobile-toggle"
                        aria-label="Toggle menu"
                        className={`xl:hidden ml-2 p-2 rounded-full transition-colors ${
                            scrolled ? "text-navy" : "text-ivory"
                        }`}
                        onClick={() => setOpen((s) => !s)}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>
            {open && (
                <div data-testid="mobile-menu" className="xl:hidden glass border-t border-white/40 max-h-[calc(100vh-92px)] overflow-y-auto">
                    <div className="max-w-[1600px] mx-auto px-6 py-5 flex flex-col gap-2">
                        {links.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-navy py-2.5 text-sm uppercase tracking-[0.28em] font-light hover:text-gold"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <Link to="/booking"
                            data-testid="mobile-nav-book-cta"
                            className="mt-3 btn-gold text-center px-8 py-3 rounded-full text-sm uppercase tracking-[0.28em]"
                        >
                            Book Now
                        </Link>
                        {user ? (
                            <Link to="/account" data-testid="mobile-nav-account" className="text-navy py-2.5 text-sm uppercase tracking-[0.28em] font-light hover:text-gold flex items-center gap-2">
                                <User size={14} /> My account
                            </Link>
                        ) : (
                            <div className="flex flex-col gap-1 border-t border-navy/10 pt-2 mt-1">
                                <Link to="/signin" data-testid="mobile-nav-signin" className="text-navy py-2.5 text-sm uppercase tracking-[0.28em] font-light hover:text-gold">
                                    Sign in
                                </Link>
                                <Link to="/signup" data-testid="mobile-nav-signup" className="text-navy py-2.5 text-sm uppercase tracking-[0.28em] font-light hover:text-gold">
                                    Sign up
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
